import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail } from "lucide-react";
import { POLICIES } from "@/lib/policies";
import { SITE } from "@/lib/site";
import { usePageMeta } from "@/lib/seo";
import Reveal from "@/components/Reveal";

const PolicyPage = ({ kind }) => {
  const p = POLICIES[kind];
  usePageMeta({ title: p.metaTitle, description: p.metaDescription });

  return (
    <main className="pt-[72px]" data-testid={`policy-page-${kind}`}>
      <div className="max-w-[820px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Link
          to="/"
          data-testid="policy-back-link"
          className="inline-flex items-center gap-2 text-[14px] font-medium text-slate-500 hover:text-ink transition-colors duration-200"
        >
          <ArrowLeft size={16} />
          Back to home
        </Link>

        <Reveal>
          <h1
            className="mt-10 font-display font-extrabold text-ink tracking-[-0.02em] text-[clamp(2.2rem,5vw,3.6rem)] leading-[1.02]"
            data-testid={`policy-title-${kind}`}
          >
            {p.title}
          </h1>
          <p className="mt-4 font-mono text-[11px] tracking-[0.26em] uppercase text-slate-400" data-testid={`policy-updated-${kind}`}>
            Effective {p.updated}
          </p>
        </Reveal>

        <div className="mt-12 space-y-10">
          {p.sections.map((s, i) => (
            <Reveal key={s.h} delay={Math.min(i * 0.03, 0.2)}>
              <section>
                <h2 className="font-display font-bold text-[20px] md:text-[22px] tracking-tight text-ink">
                  {s.h}
                </h2>
                {s.paras.map((t, j) => (
                  <p key={j} className="mt-4 text-[15.5px] leading-relaxed text-slate-600">
                    {t}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 border border-slate-200 rounded-2xl bg-surface p-7 md:p-8" data-testid="policy-contact-card">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">
            Reach us
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-4 sm:gap-10">
            <a
              href={SITE.phoneHref}
              data-testid="policy-contact-phone"
              className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-ink hover:text-accent transition-colors duration-200"
            >
              <Phone size={16} className="text-accent" />
              {SITE.phoneDisplay}
            </a>
            <a
              href={SITE.emailHref}
              data-testid="policy-contact-email"
              className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-ink hover:text-accent transition-colors duration-200 break-all"
            >
              <Mail size={16} className="text-accent" />
              {SITE.email}
            </a>
          </div>
          <p className="mt-4 text-[13.5px] text-slate-500">{SITE.name}, {SITE.addressFull}</p>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[13px]" data-testid="policy-cross-links">
          {Object.values(POLICIES)
            .filter((x) => x.slug !== p.slug)
            .map((x) => (
              <Link
                key={x.slug}
                to={x.slug}
                data-testid={`policy-link-${x.slug.replace("/", "")}`}
                className="text-slate-500 hover:text-accent transition-colors duration-200"
              >
                {x.title}
              </Link>
            ))}
        </div>
      </div>
    </main>
  );
};

export default PolicyPage;
